import { DEFAULT_LOCALE, isNoteName, type SupportedLocale } from '@cavaquinho/shared';
import { FeedbackSurface } from '../../../ui/components/FeedbackSurface/FeedbackSurface.tsx';
import { noteAnnouncement, noteLabel } from '../../../lib/notes.js';
import type { NoteIdentificationPublicFeedback } from '../../../lib/api/types.js';

/**
 * The note-identification feedback panel (FR-021, FR-022, FR-069, FR-046,
 * FR-064, SC-018), composed from `FeedbackSurface` — which already carries
 * the ✓/✕ disc, the `near-miss` amber for a wrong answer, and the polite
 * live region. This file supplies exactly one thing `FeedbackSurface` cannot
 * know on its own: the two note names.
 */

/** A guard, not a cast — reused by `NoteIdentificationRenderer` for the same
    reason: `feedback` arrives as `unknown` off the wire. */
export function isNoteIdentificationFeedback(
  value: unknown,
): value is NoteIdentificationPublicFeedback {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { correctNote?: unknown; selectedNote?: unknown };
  return isNoteName(candidate.correctNote) && isNoteName(candidate.selectedNote);
}

interface FeedbackProps {
  feedback: unknown;
  locale?: SupportedLocale;
}

export function Feedback({ feedback, locale = DEFAULT_LOCALE }: FeedbackProps) {
  if (!isNoteIdentificationFeedback(feedback)) {
    throw new Error('Feedback received a payload that is not note-identification public feedback');
  }

  const { correctNote, selectedNote } = feedback;
  const outcome = correctNote === selectedNote ? 'correct' : 'miss';

  /*
   * A miss is `near-miss`, never `error` — a wrong answer is a normal outcome
   * of practice, and coral is reserved for a real system failure (§ 13).
   */
  const title = outcome === 'correct' ? 'Isso! Ouvido afiado.' : 'Não foi dessa vez';

  // FR-069 — a miss names both notes; a hit names the one note once.
  const detail: string =
    outcome === 'correct'
      ? `A nota era ${noteLabel(correctNote, locale)}.`
      : `Você escolheu ${noteLabel(selectedNote, locale)}. A nota era ${noteLabel(correctNote, locale)}.`;

  // FR-064 — the same result, in one plain sentence, for assistive technology.
  // `FeedbackSurface` renders the one polite live region this needs; it is
  // not duplicated here.
  const announcement: string =
    outcome === 'correct'
      ? `Resposta correta. ${noteAnnouncement(correctNote, locale)}.`
      : `Resposta incorreta. Você escolheu ${noteAnnouncement(selectedNote, locale)}. A nota era ${noteAnnouncement(correctNote, locale)}.`;

  /*
   * BOTH OUTCOMES TAKE THE SAME PANEL, IN THE SAME PLACE.
   *
   * The orb, the question and the resolved tiles stay put either way, and the
   * result arrives under them — so a hit and a miss are the same screen
   * reporting a different answer, rather than two screens the learner has to
   * re-read. § 4's rise in intensity on success is spent inside the panel:
   * grad/success on the surface, § 9's rings on the disc.
   */
  return (
    <div data-testid="feedback">
      <FeedbackSurface
        outcome={outcome}
        title={title}
        detail={detail}
        announcement={announcement}
      />
    </div>
  );
}
